// White-label colour rules from the Claude Design "Acesso" prototype: a tenant only supplies one
// brand colour, and every surface derives from it while keeping WCAG AA contrast (4.5:1).

const HEX = /^#([0-9a-f]{6})$/i

export function isHexColor(value: string | null | undefined): value is string {
  return !!value && HEX.test(value)
}

function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5]
    .map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)))
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!
}

export function contrastRatio(a: string, b: string): number {
  const x = luminance(a)
  const y = luminance(b)
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

// Black or white, whichever reads better on the colour.
export function onColor(hex: string): '#FFFFFF' | '#000000' {
  return contrastRatio(hex, '#FFFFFF') >= contrastRatio(hex, '#000000') ? '#FFFFFF' : '#000000'
}

export interface BrandPalette {
  // Primary button background and text.
  brand: string
  onBrand: string
  // Logo tile (initials) background and text.
  tile: string
  onTile: string
  // Links and focus ring.
  link: string
}

// No colour (console, neutral identity) keeps the design-system accent.
export function brandPalette(hex: string | null | undefined, dark: boolean): BrandPalette {
  if (!isHexColor(hex)) {
    return {
      brand: 'var(--accent)',
      onBrand: 'var(--on-accent, #FFFFFF)',
      tile: 'var(--label)',
      onTile: 'var(--system-background)',
      link: 'var(--accent)',
    }
  }

  // A colour too light for white text becomes a black (light) / white (dark) button.
  const readable = contrastRatio(hex, '#FFFFFF') >= 4.5
  return {
    brand: readable ? hex : dark ? '#FFFFFF' : '#000000',
    onBrand: readable ? '#FFFFFF' : dark ? '#000000' : '#FFFFFF',
    tile: hex,
    onTile: onColor(hex),
    link: contrastRatio(hex, dark ? '#1C1C1E' : '#FFFFFF') >= 4.5 ? hex : 'var(--label)',
  }
}

// CSS custom properties for a container (see AccessCard).
export function brandStyle(palette: BrandPalette): Record<string, string> {
  return {
    '--brand': palette.brand,
    '--on-brand': palette.onBrand,
    '--brand-tile': palette.tile,
    '--on-tile': palette.onTile,
    '--brand-link': palette.link,
  }
}

// Same rule as the backend (Tenant.InitialsOf): first letters of the first two words; a one-word
// name or a first word in capitals (acronym) gives its first two letters.
// "Verde Campo Agro" → "VC"; "ACME Distribuidora" → "AC"; "Assistente" → "AS".
export function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean)
  if (words.length === 0) return ''
  const first = words[0]!
  const acronym = first.length >= 2 && first === first.toUpperCase() && /\p{L}/u.test(first)
  const initials = words.length === 1 || acronym ? first.slice(0, 2) : first[0]! + words[1]![0]!
  return initials.toUpperCase()
}
